// NWPS flood categories (NOAAGaugeSeverity.swift) with the colors the current
// web app and legend already use. Order of severityLevel matches iOS.

export type FloodCategory =
  | 'low_water'
  | 'no_flooding'
  | 'action'
  | 'minor'
  | 'moderate'
  | 'major'
  | 'record'
  | 'unknown';

export const FLOOD_CATEGORY_INFO: Record<FloodCategory, { label: string; color: string; severityLevel: number }> = {
  unknown: { label: 'Unknown', color: '#6c91cf', severityLevel: -1 },
  low_water: { label: 'Low Water', color: '#87cefa', severityLevel: 0 },
  no_flooding: { label: 'No Flooding', color: '#4caf50', severityLevel: 1 },
  action: { label: 'Action Stage', color: '#ffffff', severityLevel: 2 },
  minor: { label: 'Minor Flooding', color: '#fbc02d', severityLevel: 3 },
  moderate: { label: 'Moderate Flooding', color: '#f57c00', severityLevel: 4 },
  major: { label: 'Major Flooding', color: '#d32f2f', severityLevel: 5 },
  record: { label: 'Record Flooding', color: '#8c008c', severityLevel: 6 },
};

/** Legend rows, most to least severe then unknown (FloodLegendView.swift). */
export const LEGEND_GAUGE_CATEGORIES: FloodCategory[] = ['major', 'moderate', 'minor', 'action', 'no_flooding', 'unknown'];

export function parseFloodCategory(raw: string | null | undefined): FloodCategory {
  switch (raw) {
    case 'low_water':
    case 'no_flooding':
    case 'action':
    case 'minor':
    case 'moderate':
    case 'major':
    case 'record':
      return raw;
    default:
      return 'unknown';
  }
}

export function categoryLevel(c: FloodCategory | null | undefined): number {
  return FLOOD_CATEGORY_INFO[c ?? 'unknown'].severityLevel;
}

/** True if any degree of flooding is occurring or forecast (severityLevel >= minor). */
export function categoryIsFlooding(c: FloodCategory | null | undefined): boolean {
  return categoryLevel(c) >= FLOOD_CATEGORY_INFO.minor.severityLevel;
}

export function categoryColor(c: FloodCategory | null | undefined): string {
  return FLOOD_CATEGORY_INFO[c ?? 'unknown'].color;
}

export function categoryLabel(c: FloodCategory | null | undefined): string {
  return FLOOD_CATEGORY_INFO[c ?? 'unknown'].label;
}

export const GAUGE_CACHE = {
  regionLifetimeMs: 6 * 60 * 60 * 1000,
  severityLifetimeMs: 10 * 60 * 1000,
  /** NWPS answers 404 for USGS sites that are not forecast points; remember that for a day instead of re-asking on every camera move. */
  notFoundLifetimeMs: 24 * 60 * 60 * 1000,
  fetchPaddingMultiplier: 2.0,
  maximumConcurrentSeverityLoads: 20,
  visibleRegionPaddingFraction: 0.5,
  severityPublishBatchMs: 500,
  arcgisPageSize: 2000,
  arcgisMaximumPages: 10,
} as const;

export const STATION_PIN_COLOR = '#6bbaff';
